import React, { useState } from "react";
//#region types
import { FileGridListItem } from "models/utilTypes/fileUtilsTypes";
//#endregion
//#region components
import { Avatar, Grid, IconButton, LinearProgress, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useImageService } from "serviceHooks";
import Icon from "../icon";
import SimpleTooltip from "../tooltips/tooltip";
//#endregion
//#region helpers
import { FileHelper, UtilsHelper } from "utils/helpers";
import { FileEnums } from "models/enums";
import { isHeicOrHeif } from "utils/helpers/heicToJpegHelper";
//#endregion
import NoImage from "images/no_image.png";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { IconDefinitionConstants } from "utils/constants";

const FilesGridItem: React.FC<FileGridListItem> = (props) => {
  const imageService = useImageService();
  const isImage = props.fileMeta?.contentType ? FileHelper.isImage(props.fileMeta.contentType) : false;

  const [size, setSize] = useState<string>("");
  const [convertedImageSrc, setConvertedImageSrc] = useState<string | undefined>(undefined);
  const [isConverting, setIsConverting] = useState<boolean>(false);

  React.useEffect(() => {
    if (props.sizeInBytes) setSize(FileHelper.bytesToSize(props.sizeInBytes));
    else if (props.id && !UtilsHelper.isClientGeneratedId(Number(props.id))) {
      imageService.getFileSize(props.id.toString()).then((res) => {
        setSize(FileHelper.bytesToSize(res));
      });
    }
  }, [props.id, props.sizeInBytes]);

  React.useEffect(() => {
    let cancelled = false;

    const convertHeicIfNeeded = async () => {
      if (
        isImage &&
        props.fileMeta &&
        props.fileMeta.content &&
        props.fileMeta.content.length > 0 &&
        props.fileMeta.contentType
      ) {
        // Check if HEIC/HEIF conversion is needed
        if (isHeicOrHeif(props.fileMeta.contentType)) {
          if (!cancelled) {
            setIsConverting(true);
          }
          try {
            const { convertHeicToJpeg } = await import("utils/helpers/heicToJpegHelper");
            const convertedData = await convertHeicToJpeg(props.fileMeta.content);
            if (!cancelled) {
              setConvertedImageSrc(`data:${FileEnums.ContentTypes.ImageJPEG};base64,${convertedData}`);
              setIsConverting(false);
            }
          } catch (error) {
            console.error("Failed to convert HEIC image:", error);
            if (!cancelled) {
              setConvertedImageSrc(undefined);
              setIsConverting(false);
            }
          }
        }
        // For non-HEIC images, convertedImageSrc and isConverting remain in initial state (undefined/false)
        // which is correct since they're only meaningful for HEIC files
      } else {
        if (!cancelled) {
          setConvertedImageSrc(undefined);
        }
      }
    };

    convertHeicIfNeeded();

    return () => {
      cancelled = true;
    };
  }, [props.fileMeta, isImage]);

  let fileIcon;
  if (!isImage) {
    fileIcon = FileHelper.getIconTextByContentType(props.fileMeta?.contentType);
  }

  const fileName = !UtilsHelper.isEmpty(props.name)
    ? props.name
    : FileHelper.getEmptyFileName(props.fileMeta?.contentType ?? "");

  const onCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    props.handleClose && props.handleClose();
  };

  return (
    <Grid
      item
      container
      className={clsx(
        cssExports.filesGridItem,
        isImage ? cssExports.imageGridItem : "",
        props.progressBar ? cssExports.progressGridItem : ""
      )}
      direction="row"
      onClick={props.onTileClick}
    >
      {props.fileMeta && (
        <Grid xs item container className={cssExports.filesGridItemContent} direction="row">
          <Grid item>
            {isImage ? (
              <img
                className={clsx(cssExports.uploadedFileIcon, cssExports.uploadedFileImage)}
                src={
                  convertedImageSrc
                    ? convertedImageSrc
                    : isConverting || isHeicOrHeif(props.fileMeta.contentType)
                      ? NoImage
                      : props.fileMeta.content?.length > 0
                        ? `data:${props.fileMeta.contentType};base64,${props.fileMeta.content}`
                        : NoImage
                }
              />
            ) : (
              <Avatar
                className={clsx(
                  cssExports.uploadedFileIcon,
                  fileIcon?.iconColor,
                  fileIcon?.iconText && fileIcon.iconText.length > 3 ? cssExports.uploadedFileIconSmall : ""
                )}
              >
                {fileIcon?.iconText}
              </Avatar>
            )}
          </Grid>

          <Grid item container xs className={cssExports.fileMetaText} direction="column" justifyContent="center">
            <Tooltip disableInteractive title={fileName!} placement="top">
              <label
                className={clsx(cssExports.fileMetaName, props.onFileClick ? cssExports.cursorPointer : "")}
                onClick={props.onFileClick && props.onFileClick.bind(null, props.id)}
              >
                {fileName}
              </label>
            </Tooltip>
            <label className={cssExports.fileSubText}>{size}</label>
          </Grid>
        </Grid>
      )}

      {props.progressBar && (
        <Grid item container wrap="nowrap" className={cssExports.progressBarContainer} xs>
          <LinearProgress
            classes={{
              root: cssExports.borderLinearProgress,
              bar: cssExports.borderLinearProgressBar,
            }}
            variant="determinate"
            value={props.progress}
          />
        </Grid>
      )}

      {props.locked && (
        <Grid item>
          <SimpleTooltip text={props.lockedText} showOnlyOnOverflow={false}>
            <div className={clsx(cssExports.lockedIcon)}>
              <Icon
                icon={IconDefinitionConstants.WLock}
              />
            </div>
          </SimpleTooltip>
        </Grid>
      )}

      {!props.hideRemove && (
        <Grid item>
          <IconButton
            className={clsx(cssExports.closeIcon)}
            onClick={onCloseClick}
            disabled={props.progress !== undefined && props.progress > 99}
          >
            <CloseIcon />
          </IconButton>
        </Grid>
      )}
    </Grid>
  );
};

export default FilesGridItem;
